import { useState } from 'react';
import { Alert, Button, Form, Input, Modal, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { spacing } from '../../theming';
import { useImpersonationService } from '../../services/impersonationService';

const ERROR_CODES = [
    'STEP_UP_FAILED',
    'TOTP_LOCKED',
    'TARGET_NOT_ALLOWED',
    'TOO_MANY_SESSIONS',
    'IMPERSONATION_DISABLED',
];

/**
 * Starts an impersonation (#790): a reason and a fresh 2FA code, then a link that opens the session in a new tab.
 * The link is a real anchor clicked by the admin (a fresh user gesture), never window.open after an await, which
 * Safari blocks; noopener keeps the new tab from reaching this one.
 */
export default function ImpersonateModal({ target, onClose }) {
    const { t } = useTranslation('impersonation');
    const { startSession, revokeSession } = useImpersonationService();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [session, setSession] = useState(null);

    const onSubmit = async values => {
        setLoading(true);
        setError(null);
        try {
            setSession(
                await startSession({
                    realm: target.realm,
                    targetId: target.id,
                    reason: values.reason,
                    totp: values.totp,
                })
            );
        } catch (caught) {
            const code = caught?.response?.data?.code;
            setError(ERROR_CODES.includes(code) ? code : 'GENERIC');
            form.setFieldValue('totp', '');
        } finally {
            setLoading(false);
        }
    };

    const startAgain = () => {
        setSession(null);
        form.resetFields();
    };

    // closing before the link was used ends the session, so no unused session waits out its 30 minutes
    const onCancel = async () => {
        if (session) {
            try {
                await revokeSession(session.sessionId);
            } catch (caught) {
                // already opened or ended: nothing to revoke
            }
        }
        onClose();
    };

    return (
        <Modal
            open
            title={t('modal.title', { name: target.name || target.email })}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            // App.scss zeroes the padding of every modal; the app's padded dialogs use the same spacing
            styles={{ content: { padding: `${spacing[5]} ${spacing[4]}` } }}
        >
            {session ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert type="success" showIcon message={t('modal.ready')} description={t('modal.linkExpires')} />
                    <Button
                        type="primary"
                        size="large"
                        href={session.handoffUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClose}
                    >
                        {t('modal.open')}
                    </Button>
                    <Button type="link" onClick={startAgain}>
                        {t('modal.startAgain')}
                    </Button>
                </Space>
            ) : (
                <Form form={form} layout="vertical" onFinish={onSubmit} requiredMark={false}>
                    <Typography.Paragraph type="secondary">{t('modal.explanation')}</Typography.Paragraph>
                    {error && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={t(`modal.errors.${error}`)}
                        />
                    )}
                    <Form.Item
                        name="reason"
                        label={t('modal.reason')}
                        rules={[{ required: true, min: 10, max: 500, message: t('modal.reasonRule') }]}
                    >
                        <Input.TextArea rows={3} maxLength={500} showCount placeholder={t('modal.reasonPlaceholder')} />
                    </Form.Item>
                    <Form.Item
                        name="totp"
                        label={t('modal.totp')}
                        rules={[{ required: true, pattern: /^\d{6}$/, message: t('modal.totpRule') }]}
                    >
                        <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block>
                        {t('modal.start')}
                    </Button>
                </Form>
            )}
        </Modal>
    );
}
